import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext'; // Adjust the path as needed
import '../styles/Register.css';
import { useTranslation } from 'react-i18next';
import {BASE_URL} from "../config";

const Register = () => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        password: '',
        confirmPassword: '',
        address: '',
        gender: '',
    });

    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const navigate = useNavigate();
    const { login } = useAuth(); // Get the login function from AuthContext

    // Handle input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error when user types
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    // Validate email format
    const validateEmail = (email) => {
        const regex = /^\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/;
        return regex.test(email);
    };

    // Validate phone format
    const validatePhone = (phone) => {
        const regex = /^(?:\+964|00964|\+1|001)([0-9]{10})$/;
        return regex.test(phone);
    };

    // Validate password strength
    const validatePassword = (password) => {
        const regex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%^&*#]{8,20}$/;
        return regex.test(password);
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};

        // Name validation
        if (!formData.name) {
            newErrors.name = 'Full name is required';
        } else if (formData.name.length < 7) {
            newErrors.name = 'Full name must be at least 6 characters';
        }

        // Email validation
        if (!formData.email) {
            newErrors.email = 'Email is required';
        } else if (!validateEmail(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        // Phone validation
        if (!formData.phone) {
            newErrors.phone = 'Phone number is required';
        } else if (!validatePhone(formData.phone)) {
            newErrors.phone = 'Invalid phone number format (e.g., +9647500000000)';
        }

        // Password validation
        if (!formData.password) {
            newErrors.password = 'Password is required';
        } else if (formData.password.length < 8) {
            newErrors.password = 'Password must be at least 8 characters';
        } else if (!validatePassword(formData.password)) {
            newErrors.password = 'Password must contain at one number, one letter';
        }

        // Confirm password validation
        if (!formData.confirmPassword) {
            newErrors.confirmPassword = 'Please confirm your password';
        } else if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        // Address validation
        if (!formData.address) {
            newErrors.address = 'Address is required';
        } else if (formData.address.length < 7) {
            newErrors.address = 'Address must be at least 6 characters';
        }

        // Gender validation
        if (!formData.gender) {
            newErrors.gender = 'Please select your gender';
        }

        // Terms validation
        if (!agreedToTerms) {
            newErrors.terms = 'You must agree to the terms and conditions';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle form submission
    const handleRegister = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        setIsLoading(true);

        try {
            const response = await fetch(`${BASE_URL}api/v1/register`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify({
                    name: formData.name,
                    email: formData.email,
                    phone: formData.phone,
                    password: formData.password,
                    password_confirmation: formData.confirmPassword, // Changed to match Laravel expectation
                    address: formData.address,
                    gender: parseInt(formData.gender),
                }),
            });

            const responseText = await response.text();
            console.log('Response text:', responseText);

            let responseData;
            try {
                responseData = JSON.parse(responseText);
            } catch (parseError) {
                console.error('Failed to parse JSON:', parseError);
                throw new Error(`Server returned invalid JSON: ${responseText}`);
            }

            if (response.ok) {
                console.log('Registration successful:', responseData);

                // Check if we received an authentication token
                if (responseData.token) {
                    // Automatically log the user in using the AuthContext
                    login({
                        token: responseData.token,
                        name: formData.name,
                        phone: formData.phone,
                        email: formData.email,
                        address: formData.address,
                        gender: parseInt(formData.gender)
                    });

                    // Show success message
                    setErrors({
                        success: 'Registration successful! You are now logged in.'
                    });

                    // Redirect to home/dashboard after a short delay
                    setTimeout(() => {
                        navigate('/'); // Redirect to home page
                    }, 2000);
                } else {
                    // Show success message but redirect to login
                    setErrors({
                        success: 'Registration successful! You can now login to your account.'
                    });

                    // Redirect to login after a delay
                    setTimeout(() => {
                        navigate('/login');
                    }, 3000);
                }

                // Reset form
                setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    password: '',
                    confirmPassword: '',
                    address: '',
                    gender: '',
                });
                setAgreedToTerms(false);
            } else {
                console.log('Registration failed:', responseData);

                // Handle validation errors from server
                if (responseData.errors) {
                    const serverErrors = {};
                    responseData.errors.forEach(error => {
                        serverErrors[error.field] = error.errors.join(', ');
                    });
                    setErrors(serverErrors);
                } else {
                    setErrors({
                        general: responseData.message || `Registration failed (${response.status})`
                    });
                }
            }
        } catch (error) {
            console.error('Registration error:', error);
            setErrors({ general: error.message || 'Network error. Please try again.' });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="register-page">
            {/* Register Form */}
            <div className="register-form container section-padding text-center">
                <h2 className="text-color-bright mb-5">{t('Create New Account')}</h2>

                {errors.general && (
                    <div className="alert alert-danger" role="alert">
                        {errors.general}
                    </div>
                )}

                {errors.success && (
                    <div className="alert alert-success" role="alert">
                        {errors.success}
                    </div>
                )}

                <form onSubmit={handleRegister}>
                    <div className="row gy-3 mb-3">
                        <div className="col-md-4">
                            <div className="form-floating">
                                <input
                                    type="text"
                                    className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                    id="fullnameInput"
                                    placeholder="Fullname"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                />
                                <label htmlFor="fullnameInput">{t('Fullname')}</label>
                                {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="form-floating">
                                <input
                                    type="email"
                                    className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                                    id="emailInput"
                                    placeholder="name@example.com"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                />
                                <label htmlFor="emailInput">{t('Email address')}</label>
                                {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="form-floating">
                                <input
                                    type="tel"
                                    className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                                    id="phoneInput"
                                    placeholder="Phone Number"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                />
                                <label htmlFor="phoneInput">{t('Phone Number')}</label>
                                {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
                            </div>
                        </div>
                    </div>

                    <div className="row gy-3 mb-3">
                        <div className="col-md-4">
                            <div className="form-floating">
                                <input
                                    type="password"
                                    className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                                    id="passwordInput"
                                    placeholder="Password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleInputChange}
                                />
                                <label htmlFor="passwordInput">{t('Password')}</label>
                                {errors.password && <div className="invalid-feedback">{errors.password}</div>}
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="form-floating">
                                <input
                                    type="password"
                                    className={`form-control ${errors.confirmPassword ? 'is-invalid' : ''}`}
                                    id="confirmPassInput"
                                    placeholder="Confirm Password"
                                    name="confirmPassword"
                                    value={formData.confirmPassword}
                                    onChange={handleInputChange}
                                />
                                <label htmlFor="confirmPassInput">{t('Confirm Password')}</label>
                                {errors.confirmPassword && <div className="invalid-feedback">{errors.confirmPassword}</div>}
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="form-floating">
                                <input
                                    type="text"
                                    className={`form-control ${errors.address ? 'is-invalid' : ''}`}
                                    id="addressInput"
                                    placeholder="Address"
                                    name="address"
                                    value={formData.address}
                                    onChange={handleInputChange}
                                />
                                <label htmlFor="addressInput">Address</label>
                                {errors.address && <div className="invalid-feedback">{errors.address}</div>}
                            </div>
                        </div>
                    </div>

                    <div className="gender-selection mb-3">
                        <div className="d-flex justify-content-center gap-3">
                            <input
                                type="radio"
                                className="btn-check"
                                name="gender"
                                id="male"
                                value="0"
                                checked={formData.gender === "0"}
                                onChange={handleInputChange}
                            />
                            <label className="btn gender-btn" htmlFor="male">{t('Male')}</label>

                            <input
                                type="radio"
                                className="btn-check"
                                name="gender"
                                id="female"
                                value="1"
                                checked={formData.gender === "1"}
                                onChange={handleInputChange}
                            />
                            <label className="btn gender-btn" htmlFor="female">{t('Female')}</label>
                        </div>
                        {errors.gender && <div className="text-danger mt-1">{errors.gender}</div>}
                    </div>

                    <div className="form-check d-flex justify-content-center mt-2">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            id="flexCheckDefault"
                            checked={agreedToTerms}
                            onChange={(e) => setAgreedToTerms(e.target.checked)}
                        />
                        <label className="form-check-label ms-2" htmlFor="flexCheckDefault">
                            {t('I agree to the')}
                            <Link to="/termsandconditions" target="_blank" className="ms-1">
                                {t('Terms and Conditions')}
                            </Link>
                        </label>
                    </div>
                    {errors.terms && <div className="text-danger mt-1">{errors.terms}</div>}

                    <button
                        type="submit"
                        className="btn btn-custom mt-3 py-2 px-4"
                        disabled={isLoading}
                    >
                        {isLoading ? 'Registering...' : `${t('Register')}`}
                    </button>
                </form>
            </div>

            <div className="new-account w-100 my-4 text-center">
                {t('Already Have an Account?')} <Link to="/login">{t('Login Here')}</Link>
            </div>
        </div>
    );
};

export default Register;